import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { isPractitionerMediatedConsentFlow, isPrimaryConsentFlow } from '../../../utils/consentFlowHelper'
import FamilyPortalAPIClient from '../../auth/FamilyPortalAPIClient'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { usePreferencesContext } from '../../preferences/usePreferencesContext'
import type { ConsentContext } from '../consentOptions/types'

type EntryPointRouting = { loading: true; redirectTo: null } | { loading: false; redirectTo: string | null }

function isSecondaryConsentComplete(state: ConsentContext['secondaryConsentState']): boolean {
  if (!state) return false
  const requiredKeys = Object.keys(state.itemStates).filter((k) => !k.startsWith('ACCOUNT_PREF_'))
  return requiredKeys.every((k) => k in state.itemMetadata)
}

async function resolveOnboardingRedirect(
  apiClient: FamilyPortalAPIClient,
  needsLanguage: boolean
): Promise<string | null> {
  if (needsLanguage) return '/language-select'

  try {
    const res = await apiClient.get('/profile/demographics')
    if (!res.ok) return '/dob-capture'
    const { dateOfBirth }: { dateOfBirth?: string | null } = await res.json()
    return !dateOfBirth ? '/dob-capture' : null
  } catch {
    return '/dob-capture'
  }
}

/** Re-resolve routing after leaving onboarding so consent completion is picked up. */
export function shouldRefetchEntryPointRouting(
  previousPathname: string | null,
  pathname: string,
  pathnameChanged: boolean
): boolean {
  if (previousPathname === null) return true
  if (!pathnameChanged) return true
  const wasOnboarding = previousPathname === '/intro' || previousPathname.startsWith('/consents')
  const isOnboarding = pathname === '/intro' || pathname.startsWith('/consents')
  return wasOnboarding && !isOnboarding
}

async function resolveEntryPointRedirect(
  apiClient: FamilyPortalAPIClient,
  needsLanguage: boolean
): Promise<string | null> {
  const onboarding = await resolveOnboardingRedirect(apiClient, needsLanguage)
  if (onboarding) return onboarding

  let isFirstLogin = true

  try {
    const firstLoginRes = await apiClient.get('/auth/check-first-login', undefined, true)
    if (firstLoginRes.ok) {
      const data: { isFirstLogin: boolean; lastLogin: string | null; loginsCount: number } = await firstLoginRes.json()
      isFirstLogin = data.isFirstLogin
    }
  } catch {
    isFirstLogin = true
  }

  try {
    const contextRes = await apiClient.get('/consents/context', undefined, true)

    if (contextRes.status === 404 || !contextRes.ok) {
      return isFirstLogin ? '/intro' : null
    }

    const context: ConsentContext = await contextRes.json()
    const { consentFlow, primaryConsentId, primaryConsentSignedAt, secondaryConsentState } = context
    const practitionerMediated = isPractitionerMediatedConsentFlow(consentFlow)
    const cttSigned = primaryConsentId != null || primaryConsentSignedAt != null

    if (!cttSigned && !practitionerMediated) {
      return '/intro'
    }

    if (practitionerMediated) {
      if (isFirstLogin) return '/intro'
      const secondaryComplete = isSecondaryConsentComplete(secondaryConsentState)
      return secondaryComplete ? null : '/consents/secondary'
    }

    if (!isPrimaryConsentFlow(consentFlow)) {
      Sentry.captureMessage(`useEntryPointRouting: unrecognized consentFlow "${String(consentFlow)}"`, 'warning')
      return isFirstLogin ? '/intro' : null
    }

    const secondaryComplete = isSecondaryConsentComplete(secondaryConsentState)
    return secondaryComplete ? null : '/consents/secondary'
  } catch {
    return isFirstLogin ? '/intro' : null
  }
}

export function useEntryPointRouting(): EntryPointRouting {
  const apiClient = useFamilyPortalApiClient()
  const { pathname } = useLocation()
  const { state: preferences } = usePreferencesContext()
  const [routing, setRouting] = useState<EntryPointRouting>({ loading: true, redirectTo: null })
  const previousPathnameRef = useRef<string | null>(null)
  const resolveGenerationRef = useRef(0)
  const lastResolvedRef = useRef<Extract<EntryPointRouting, { loading: false }>>({ loading: false, redirectTo: null })

  const needsLanguage = preferences.status === 'ready' && preferences.stored === null

  useEffect(() => {
    const previousPathname = previousPathnameRef.current
    const pathnameChanged = previousPathname !== null && previousPathname !== pathname
    previousPathnameRef.current = pathname

    if (!shouldRefetchEntryPointRouting(previousPathname, pathname, pathnameChanged)) {
      setRouting((prev) => (prev.loading ? lastResolvedRef.current : prev))
      return
    }

    const generation = ++resolveGenerationRef.current
    setRouting({ loading: true, redirectTo: null })

    resolveEntryPointRedirect(apiClient, needsLanguage)
      .then((redirectTo) => {
        if (generation !== resolveGenerationRef.current) return
        const next = { loading: false, redirectTo } as const
        lastResolvedRef.current = next
        setRouting(next)
      })
      .catch(() => {
        if (generation !== resolveGenerationRef.current) return
        const next = { loading: false, redirectTo: null } as const
        lastResolvedRef.current = next
        setRouting(next)
      })

    return undefined
  }, [apiClient, needsLanguage, pathname])

  // When transitioning out of the onboarding flow (e.g. /consents/* → /home), there is one
  // render where routing still holds the stale redirectTo from the previous resolution but the
  // new effect hasn't fired yet to reset it to loading. The guard would see redirectTo='/intro'
  // and immediately navigate before gen+1 can resolve. Detect this synchronously during render
  // by reading the ref (which still holds the previous pathname) and override to loading=true so
  // the guard shows a spinner instead.
  const prevPathname = previousPathnameRef.current
  const pathnameChangedNow = prevPathname !== null && prevPathname !== pathname
  if (pathnameChangedNow && !routing.loading) {
    const wasOnboarding = prevPathname === '/intro' || prevPathname!.startsWith('/consents')
    const isOnboarding = pathname === '/intro' || pathname.startsWith('/consents')
    if (wasOnboarding && !isOnboarding) {
      return { loading: true, redirectTo: null }
    }
  }

  return routing
}
